import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  SimpleGrid,
  Text,
  Center,
  Spinner,
  Container,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { Icon } from 'src/components/atoms/Icon';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { useAppContext } from 'src/contexts/AppContext';
import {
  useGoalsQuery,
  useGoalDetailQuery,
  useCreateGoalMutation,
  useDeleteGoalMutation,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
} from 'src/api/goals';
import { GoalCard } from 'src/components/molecules/GoalCard';
import { GoalDrawer } from 'src/features/Goals/components/molecules/GoalDrawer';
import { GoalDetailsDrawer } from 'src/features/Goals/components/molecules/GoalDetailsDrawer';
import { DeleteGoalDialog } from 'src/features/Goals/components/molecules/DeleteGoalDialog';
import { EmptyState } from 'src/features/Goals/components/templates/EmptyState';
import {
  Goal,
  GoalDetail,
  GoalsView,
  CreateGoalRequest,
} from 'src/features/Goals/types';
import { MODEL_TYPE_PATHS } from 'src/features/Goals/utils/formatters';
import { useCampaignGroupsQuery } from 'src/api/campaignGroups';
import { useUsersQuery } from 'src/api/users';

export const GoalsPage: React.FC = () => {
  const pageBg = useColorModeValue('white', 'background.dark.lowest');
  const navigate = useNavigate();
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName ?? '';
  const { toastState, showSuccess, showError, hideToast } = useCustomToast();

  // View State
  const [view, setView] = useState<GoalsView>({ type: 'list' });
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<Goal | null>(null);

  // Determine which goal detail to fetch (for quick view or editing)
  const goalIdToFetch =
    selectedGoalId ?? (view.type === 'edit' ? view.goalId : null);

  // Queries
  const { data: goals = [], isLoading, isError } = useGoalsQuery(customerId);
  const { data: goalDetail, isLoading: isLoadingDetail } = useGoalDetailQuery(
    customerId,
    goalIdToFetch,
  );
  const { data: campaignGroups = [] } = useCampaignGroupsQuery(customerId);
  const { data: users = [] } = useUsersQuery(customerId);

  const campaignGroupsForDrawer = useMemo(
    () => campaignGroups.map((g) => ({ id: g.id, name: g.groupName })),
    [campaignGroups],
  );

  const usersForDrawer = useMemo(
    () =>
      users.map((u) => ({
        id: u.id,
        name: u.displayName ?? u.email,
        avatarUrl: undefined,
      })),
    [users],
  );

  // Mutations (update mutation is in GoalDrawer where goalId is known)
  const createGoalMutation = useCreateGoalMutation(customerId);
  const deleteGoalMutation = useDeleteGoalMutation(customerId);

  // Comment mutations
  const createCommentMutation = useCreateCommentMutation(
    customerId,
    selectedGoalId ?? '',
  );
  const updateCommentMutation = useUpdateCommentMutation(
    customerId,
    selectedGoalId ?? '',
  );
  const deleteCommentMutation = useDeleteCommentMutation(
    customerId,
    selectedGoalId ?? '',
  );

  // Handlers
  const handleShowList = () => setView({ type: 'list' });
  const handleShowCreate = () => setView({ type: 'create' });
  const handleShowEdit = (goalId: string) => setView({ type: 'edit', goalId });

  const handleViewDetails = (goal: Goal) => {
    setSelectedGoalId(goal.id);
    setIsDetailsDrawerOpen(true);
  };

  const handleCloseDetailsDrawer = () => {
    setIsDetailsDrawerOpen(false);
    setSelectedGoalId(null);
  };

  const handleViewLinkedData = (goal: Goal | GoalDetail) => {
    const path = MODEL_TYPE_PATHS[goal.modelType];
    navigate(path, {
      state: {
        filteredByGoal: goal.id,
        modelParametersId: goal.modelParametersId,
        timeRange: {
          start: goal.goalPeriodStart,
          end: goal.goalPeriodEnd,
        },
        // Pass campaigns only if available (GoalDetail has full array, Goal has only count)
        campaigns:
          'campaigns' in goal && goal.campaigns
            ? goal.campaigns.map((c) => c.id)
            : undefined,
        mediaChannels: goal.mediaChannels,
      },
    });
  };

  const handleEdit = (goal: Goal) => handleShowEdit(goal.id);

  const handleDeleteClick = (goal: Goal) => {
    setGoalToDelete(goal);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!goalToDelete) return;
    try {
      await deleteGoalMutation.mutateAsync(goalToDelete.id);
      showSuccess('Goal deleted', 'The goal has been removed successfully.');
      setIsDeleteDialogOpen(false);
      setGoalToDelete(null);
      if (selectedGoalId === goalToDelete.id) {
        handleCloseDetailsDrawer();
      }
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || 'Unable to delete the goal. Please try again.';
      showError('Delete failed', message);
    }
  };

  const handleCreateSubmit = async (data: CreateGoalRequest) => {
    try {
      await createGoalMutation.mutateAsync(data);
      showSuccess('Goal created', 'Your goal has been created successfully.');
      handleShowList();
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || 'Unable to create the goal. Please try again.';
      showError('Create failed', message);
    }
  };

  const handleUpdateSuccess = () => {
    showSuccess('Goal updated', 'Your goal has been updated successfully.');
    handleShowList();
  };

  const handleUpdateError = (message?: string) => {
    showError(
      'Update failed',
      message || 'Unable to update the goal. Please try again.',
    );
  };

  const handleAddComment = async (
    content: string,
    parentId?: string,
    mentionedUserIds?: string[],
  ) => {
    try {
      await createCommentMutation.mutateAsync({
        content,
        parentId,
        mentionedUserIds,
      });
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || 'Unable to add comment. Please try again.';
      showError('Comment failed', message);
    }
  };

  const handleEditComment = async (
    commentId: string,
    content: string,
    mentionedUserIds?: string[],
  ) => {
    try {
      await updateCommentMutation.mutateAsync({
        commentId,
        data: { content, mentionedUserIds },
      });
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || 'Unable to edit comment. Please try again.';
      showError('Edit failed', message);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteCommentMutation.mutateAsync(commentId);
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || 'Unable to delete comment. Please try again.';
      showError('Delete failed', message);
    }
  };

  // Get editing goal detail - uses goalDetail which is fetched when view.type === 'edit'
  const editingGoal = view.type === 'edit' ? (goalDetail ?? null) : null;

  // Render states
  if (isError) {
    return (
      <Center w="100%" py={16}>
        <Text color="red.500">
          We were unable to load goals. Please try again later.
        </Text>
      </Center>
    );
  }

  if (isLoading) {
    return (
      <Center w="100%" py={16}>
        <Spinner color="icon.green" size="lg" />
      </Center>
    );
  }

  if (goals.length === 0) {
    return (
      <>
        <EmptyState onAddNewGoal={handleShowCreate} />

        {/* Create Drawer (available from empty state) */}
        <GoalDrawer
          isOpen={view.type === 'create' && !isLoadingDetail}
          onClose={handleShowList}
          onCreateSubmit={handleCreateSubmit}
          onUpdateSuccess={handleUpdateSuccess}
          onUpdateError={handleUpdateError}
          isCreateLoading={createGoalMutation.isLoading}
          editingGoal={null}
          customer={customerId}
          campaignGroups={campaignGroupsForDrawer}
          users={usersForDrawer}
        />

        <CustomToast
          isOpen={toastState.isOpen}
          title={toastState.title}
          description={toastState.description}
          type={toastState.type}
          duration={toastState.duration}
          onClose={hideToast}
        />
      </>
    );
  }

  return (
    <>
      <Box bg={pageBg} w="100%" minH="100vh">
        <Container maxW="100%" px={12} py={8}>
          {/* Header */}
          <Flex
            justifyContent="space-between"
            alignItems="flex-start"
            mb={8}
            direction={{ base: 'column', md: 'row' }}
            gap={4}>
            <Box>
              <Text
                fontSize="32px"
                fontWeight="bold"
                fontFamily="marketingPage"
                color="text.title"
                mb={2}>
                Goals & Targets
              </Text>
              <Text fontSize="16px" color="gray.600" fontFamily="marketingPage">
                Track your project and goal with full information.
              </Text>
            </Box>
            <Button
              variant="primary"
              buttonName="add-new-goal"
              onClick={handleShowCreate}>
              <Icon iconName="plus" boxSize={4} mr={2} color="white" />
              Add New Goal
            </Button>
          </Flex>

          {/* Goals Grid */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={6}>
            {goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                onViewDetails={handleViewDetails}
                onViewLinkedData={handleViewLinkedData}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
              />
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Create/Edit Drawer */}
      <GoalDrawer
        isOpen={
          (view.type === 'create' || view.type === 'edit') && !isLoadingDetail
        }
        onClose={handleShowList}
        onCreateSubmit={handleCreateSubmit}
        onUpdateSuccess={handleUpdateSuccess}
        onUpdateError={handleUpdateError}
        isCreateLoading={createGoalMutation.isLoading}
        editingGoal={editingGoal}
        customer={customerId}
        campaignGroups={campaignGroupsForDrawer}
        users={usersForDrawer}
      />

      {/* Details Drawer (Quick View) */}
      <GoalDetailsDrawer
        isOpen={isDetailsDrawerOpen}
        onClose={handleCloseDetailsDrawer}
        goal={goalDetail ?? null}
        onViewLinkedData={handleViewLinkedData}
        onAddComment={handleAddComment}
        onEditComment={handleEditComment}
        onDeleteComment={handleDeleteComment}
        isLoadingComments={createCommentMutation.isLoading}
        users={usersForDrawer}
      />

      {/* Delete Confirmation */}
      <DeleteGoalDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        goalTitle={goalToDelete?.title ?? ''}
        isLoading={deleteGoalMutation.isLoading}
      />

      {/* Toast Notifications */}
      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        duration={toastState.duration}
        onClose={hideToast}
      />
    </>
  );
};
